import React, { useState, useEffect } from 'react';
import { Avatar, Box, Button, Container, Divider, FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { Employee } from '../../types/Employee';
import { getFirestore, doc, setDoc } from 'firebase/firestore';

const OCCUPATIONS = ['LABOR', 'DRIVER', 'TAILOR', 'TEA BOY', 'OFFICE', 'CAR WASH', 'WIELDER',
'PAINTER', 'CONSTRUCTION', 'BIG EQUIPMENT DRIVER', 'ELECTRICIAN', 'SUPERVISOR', 'CHIEF SUPERVISOR'];

type Props = {
  employee: Employee;
  onUpdate?: (type: number, occupation: string) => void;
};
export const EmployeeTransitionForm = (props: Props) => {
  const { employee, onUpdate } = props;

  const isChief = employee.type === 0;
  const isSupervisor = employee.type  === 1;
  const isEmployee = employee.type === 2;

  const [transitionType, setTransitionType] = useState(employee.type);
  const [transitionOccupation, setTransitionOccupation] = useState(employee.occupation ?? 'LABOR');

  const actionText = transitionType > employee.type ? 'Demoted' : 'Promoted';
  const fromTypeText = employee.type === 0 ? 'Chief Supervisor' : employee.type === 1 ? 'Supervisor' : 'Regular Employee';
  const toTypeText = transitionType === 0 ? 'Chief Supervisor' : transitionType === 1 ? 'Supervisor' : 'Regular Employee';

  const handleOccupationOnChange = (event: SelectChangeEvent<string>) => {
    setTransitionOccupation(event.target.value);
  };

  const handleTransitionOnClick = () => {
    if (window.confirm(`You sure you want to promote/demote employee ${employee.idNumber}?`)) {
      const data: any = {
        type: transitionType, 
        occupation: transitionOccupation,
      };
      if (transitionType === 0) {
        data.supervisor = '';
        data.chiefSupervisor = '';
      } else if (transitionType === 1) {
        data.supervisor = '';
      }

      const db =  getFirestore();
      const ref = doc(db, `Employees/${employee.idNumber}`);
      setDoc(ref, data, { merge: true }).then(() => {
        onUpdate && onUpdate(transitionType, transitionOccupation);
      });
    }
  };

  const handleResetOnClick = () => {
    setTransitionOccupation(employee.occupation);
  };

  useEffect(() => {
    if (transitionOccupation === 'SUPERVISOR')
      setTransitionType(1);
    else if (transitionOccupation === 'CHIEF SUPERVISOR')
      setTransitionType(0);
    else 
      setTransitionType(2);
  }, [transitionOccupation])

  return (
    <Container disableGutters sx={{ minWidth: 600 }}>
      <Box sx={{ padding: 3 }}>
        <Box mb={2}>
          <Stack direction='row' spacing={2}>
            <Avatar src={employee.picture} />
            <Stack>
              <Typography fontWeight='700'>{employee.name}</Typography>
              <Typography variant='caption'>{employee.idNumber}</Typography>
            </Stack>
          </Stack>
        </Box>
        <Stack marginY={4}>
          <Typography variant='caption'>Current Role</Typography>
          <Typography variant='h4'>
            {isChief ? 'Chief Supervisor' : isSupervisor ?  'Supervisor' : 'Employee'}
          </Typography>
        </Stack>
        <Stack>
          <FormControl>
            <InputLabel id="employee-occupation-select">Update Occupation</InputLabel>
            <Select
              labelId="employee-occupation-select"
              label="Update Occupation"
              // name="occupation"
              value={transitionOccupation}
              onChange={handleOccupationOnChange}
            >
              {OCCUPATIONS.map((b) => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Changes in occupation will chnage the employee's role.
            </FormHelperText>
          </FormControl>
        </Stack>
        <Stack marginTop={4}>
          <Typography variant='caption'>Action</Typography>
          {employee.type !== transitionType ? (
            <Stack>
              <Typography component='span'>
                {`${actionText} from `}
                <Typography component='span' sx={{
                  backgroundColor: theme => theme.palette.mode === 'light' ? theme.palette.primary.light : theme.palette.background.paper,
                  padding: 0.5,
                  paddingX: 1,
                  borderRadius: 2,
                }}>
                  {fromTypeText}
                </Typography> 
                {' to '} 
                <Typography component='span' sx={{
                  backgroundColor: theme => theme.palette.mode === 'light' ? theme.palette.primary.light : theme.palette.background.paper,
                  padding: 0.5,
                  paddingX: 1,
                  borderRadius: 2,
                }}>
                  {toTypeText}
                </Typography>
              </Typography>
            </Stack>
          ) : (
            <Typography>
              {transitionOccupation !== employee.occupation ? `Change from occupation from "${employee.occupation}" to "${transitionOccupation}".` : 'No changes.'}
            </Typography>
          )}
        </Stack>
      </Box>
      <Divider />
      <Stack direction='row' justifyContent='right' sx={{ padding: 3, paddingY: 1 }} spacing={1}>
        <Button onClick={handleTransitionOnClick}>Transtion</Button>
        <Button color='error' onClick={handleResetOnClick}>Reset</Button>
      </Stack>
    </Container>
  );
};
